import { Button, Spinner } from "@heroui/react";
import { type AdminEpisodeDetail, type AdminSeriesDetail, assertNever, ProductionState } from "@storyline/shared";
import { Link, useNavigate, useParams } from "react-router";
import PageHeader from "@/src/common/components/page-header";
import ContactSheet from "@/src/stories/components/contact-sheet";
import GeneratingProgress from "@/src/stories/components/generating-progress";
import GraphStep from "@/src/stories/components/graph-step";
import PipelineStepper from "@/src/stories/components/pipeline-stepper";
import PublishPanel from "@/src/stories/components/publish-panel";
import ReferencesGate from "@/src/stories/components/references-gate";
import { useEpisode } from "@/src/stories/hooks/use-episode";

const EpisodePipelinePage: React.FC = () => {
	const { seriesId = "", id = "" } = useParams();
	const navigate = useNavigate();
	const episodeState = useEpisode(id, seriesId);

	if (episodeState.isLoading) {
		return (
			<div className="flex justify-center py-16">
				<Spinner size="lg" />
			</div>
		);
	}

	if (episodeState.hasError || !episodeState.episode || !episodeState.series || !episodeState.production) {
		return (
			<div className="flex flex-col items-center gap-3 rounded-xl border border-border-soft bg-surface p-10 text-center">
				<p className="text-danger text-sm">Impossible de charger cet épisode.</p>
				<Button variant="secondary" onPress={() => void episodeState.reload()}>
					Réessayer
				</Button>
			</div>
		);
	}

	const { episode, series, production } = episodeState;

	const onDelete = (): void => {
		if (window.confirm("Supprimer cet épisode et tous ses médias ?")) {
			void episodeState.remove().then(() => navigate(`/series/${seriesId}`));
		}
	};

	const renderStep = (current: AdminEpisodeDetail, seriesDetail: AdminSeriesDetail): React.ReactNode => {
		switch (production.productionState) {
			case ProductionState.DRAFT:
			case ProductionState.STORY_REVIEW:
				return (
					<GraphStep
						episode={current}
						issues={episodeState.issues}
						transitionError={episodeState.transitionError}
						onTransition={(to) => void episodeState.transition(to)}
					/>
				);
			case ProductionState.REFERENCES_REVIEW:
				return (
					<div className="flex flex-col gap-3">
						{episodeState.transitionError ? (
							<p className="rounded-lg border border-danger/40 bg-glass p-3 text-danger text-sm">{episodeState.transitionError}</p>
						) : null}
						<ReferencesGate
							episode={current}
							series={seriesDetail}
							assets={episodeState.assets}
							onTransition={(to) => void episodeState.transition(to)}
						/>
					</div>
				);
			case ProductionState.KEYFRAMES_GENERATING:
			case ProductionState.VIDEOS_GENERATING:
				return <GeneratingProgress production={production} />;
			case ProductionState.KEYFRAMES_REVIEW:
				return (
					<ContactSheet
						episode={current}
						mode="keyframes"
						production={production}
						generations={episodeState.generations}
						backState={ProductionState.REFERENCES_REVIEW}
						nextState={ProductionState.VIDEOS_GENERATING}
						nextLabel="Générer les vidéos →"
						onRegenerate={(shotId, kind, reason) => void episodeState.regenerateFrame(shotId, kind, reason)}
						onTransition={(to) => void episodeState.transition(to)}
					/>
				);
			case ProductionState.VIDEOS_REVIEW:
				return (
					<ContactSheet
						episode={current}
						mode="videos"
						production={production}
						generations={episodeState.generations}
						backState={ProductionState.KEYFRAMES_REVIEW}
						nextState={ProductionState.READY}
						nextLabel="Marquer prêt →"
						onRegenerate={(shotId, kind, reason) => void episodeState.regenerateFrame(shotId, kind, reason)}
						onTransition={(to) => void episodeState.transition(to)}
					/>
				);
			case ProductionState.READY:
				return (
					<PublishPanel
						episode={current}
						production={production}
						onPublish={() => void episodeState.publish()}
						onUnpublish={() => void episodeState.unpublish()}
						onTransition={(to) => void episodeState.transition(to)}
						onUploadCover={(file) => void episodeState.uploadCover(file)}
					/>
				);
			default:
				return assertNever(production.productionState);
		}
	};

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title={`E${episode.episodeNumber} · ${episode.title}`}
				description={episode.synopsis}
				actions={
					<>
						<Link to={`/series/${seriesId}`} className="text-accent-lav text-sm hover:underline">
							← Retour à la série
						</Link>
						<Button variant="ghost" className="text-danger" onPress={onDelete}>
							Supprimer
						</Button>
					</>
				}
			/>
			<PipelineStepper current={production.productionState} />
			{renderStep(episode, series)}
		</div>
	);
};

export default EpisodePipelinePage;
